import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Box, Paper, Typography, IconButton, LinearProgress, linearProgressClasses, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';

/** Material UI Icons. */
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';

import { daysBetweenYmd, formatDateToDateString, getOperationToday } from 'shared/lib/dateFormatter';

/** Custom Styled Linear Progress Bar. */
const BorderLinearProgress = styled(LinearProgress)(({ theme, iscomplete }) => ({
    height: 5,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.grey[300],
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: iscomplete == 'true' ? theme.palette.grey[300] : '#00647e',
    },
}));

/** Function to return color based on the intensity of the task. */
const getColorByIntensity = (intensity, status) => (!status ? '#58a000' : intensity ? '#d92936' : '#f48704');

/** Ring that fades out around a card a live update just changed, so the change is noticeable without
 * the whole panel redrawing. Runs once per highlight and hands the shadow back to the Paper default.
 */
const liveUpdateHighlight = {
    animation: 'todoLiveUpdate 2.5s ease-out',
    '@keyframes todoLiveUpdate': {
        from: { boxShadow: '0 0 0 2px rgba(0, 100, 126, 0.65)' },
        to: { boxShadow: '0 0 0 2px rgba(0, 100, 126, 0)' },
    },
};

export default function TodoCards({
    isRecentlyUpdated = false,
    header,
    description,
    il_opus_reference_id,
    client,
    shipper,
    consignee,
    is_read,
    canMarkRead,
    onToggleRead,
    intensity,
    intensity_date,
    status,
    step,
    step_value,
    handleMove,
}) {
    /** Check if the task is complete.*/
    let isComplete = status === false;
    /** Check if the card has already been read by this user. */
    let isRead = Boolean(is_read);

    /**
     * Days left until the card's deadline, counted against the operations day (LA)
     * so a user checking from Korea sees the same badge as the LA team. Calendar-day
     * arithmetic keeps D-7 reading D-7 on the DST changeover weeks.
     */
    const calculateDaysLeft = (intensity_date) => {
        if (!intensity_date) return '';
        const today = formatDateToDateString(getOperationToday());
        const days = daysBetweenYmd(today, intensity_date);
        return Number.isNaN(days) ? '' : days;
    };

    let remaining_day = intensity_date ? calculateDaysLeft(intensity_date) : '';

    return (
        <Paper
            data-testid="todo-card"
            data-live-updated={isRecentlyUpdated ? 'true' : undefined}
            sx={{
                display: 'flex',
                width: '100%',
                minHeight: '78px',
                bgcolor: isComplete ? '#f3f4f3' : 'white',
                opacity: isRead ? 0.55 : 1,
                transition: 'opacity 0.2s',
                ...(isRecentlyUpdated && liveUpdateHighlight),
            }}
        >
            <Box
                sx={{
                    width: '2%',
                    bgcolor: getColorByIntensity(intensity, status),
                }}
            />
            <Box sx={{ width: '90%', p: 0.5, px: 1.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontWeight: 500,
                            width: '190px',
                            textDecoration: isComplete ? 'line-through' : 'none',
                        }}
                    >
                        {header}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 10,
                            fontWeight: 500,
                            color: '#003a52',
                            border: '1px solid #003a52',
                            borderRadius: 10,
                            px: 1,
                            width: '80px',
                            textAlign: 'center',
                        }}
                    >
                        {il_opus_reference_id}
                    </Typography>
                </Box>

                {(client || shipper || consignee) && (
                    <Typography
                        sx={{
                            fontSize: 10,
                            color: '#7a7a7a',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {client && (
                            <Box component="span" sx={{ fontWeight: 600, color: '#003a52' }}>
                                {client}
                            </Box>
                        )}
                        {client && (shipper || consignee) && '  ·  '}
                        {shipper}
                        {shipper && consignee && '  →  '}
                        {consignee}
                    </Typography>
                )}

                <Typography
                    sx={{
                        fontSize: 10,
                        fontWeight: 400,
                        textDecoration: isComplete ? 'line-through' : 'none',
                        whiteSpace: 'pre-line',
                    }}
                >
                    {description}
                </Typography>

                <BorderLinearProgress
                    variant="determinate"
                    value={Math.ceil(100 / step_value) * step}
                    iscomplete={isComplete ? 'true' : 'false'}
                    sx={{
                        mt: 0.5,
                    }}
                />
                {!isComplete && (
                    <Typography sx={{ fontSize: 10, textAlign: 'end', color: '#7a7a7a' }}>
                        <b>{remaining_day}</b> Days left
                    </Typography>
                )}
            </Box>
            <Box
                sx={{
                    width: '8%',
                    py: 1,
                    pr: 0.5,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Tooltip title="Go to shipment" arrow>
                    <IconButton
                        onClick={handleMove}
                        size="small"
                        sx={{
                            p: '1px',
                            color: '#003a52',
                            ':hover': { bgcolor: '#003a52', color: 'white' },
                        }}
                    >
                        <ArrowOutwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>

                {canMarkRead && (
                    <Tooltip title={isRead ? 'Mark as unread' : 'Mark as read'} arrow>
                        <IconButton
                            onClick={onToggleRead}
                            size="small"
                            sx={{
                                p: '1px',
                                color: '#003a52',
                                ':hover': { bgcolor: '#003a52', color: 'white' },
                            }}
                        >
                            {isRead ? (
                                <MarkEmailUnreadIcon sx={{ fontSize: 16 }} />
                            ) : (
                                <MarkEmailReadIcon sx={{ fontSize: 16 }} />
                            )}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Paper>
    );
}
